/**
 * API endpoints para Tables (Mesas)
 * Base URL: /api/tables/
 */

import { GET, POST, PUT, PATCH, DELETE } from "../config";

// ==================== MESAS ====================

/**
 * Listar mesas
 * @param {number} page - Número de página
 * @param {object} filters - Filtros (branch, status, area, capacity, etc.)
 * @returns {Promise} Lista de mesas
 */
export const list = async (page = 1, filters = {}) => {
  let url = `tables/tables/?page=${page}`;
  url += `&page_size=50`;

  if (filters.branch) {
    url += `&branch=${filters.branch}`;
  }

  if (filters.status) {
    url += `&status=${filters.status}`;
  }

  if (filters.area) {
    url += `&area__icontains=${encodeURIComponent(filters.area)}`;
  }

  if (filters.capacity_min) {
    url += `&capacity_min=${filters.capacity_min}`;
  }

  if (filters.capacity_max) {
    url += `&capacity_max=${filters.capacity_max}`;
  }

  if (filters.is_available !== undefined) {
    url += `&is_available=${filters.is_available}`;
  }

  if (filters.number) {
    url += `&number__icontains=${encodeURIComponent(filters.number)}`;
  }

  if (filters.is_active !== undefined) {
    url += `&is_active=${filters.is_active}`;
  }

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener una mesa por ID
 * @param {string|number} id - ID de la mesa
 * @returns {Promise} Datos de la mesa
 */
export const get = async (id) => {
  const response = await GET(`tables/tables/${id}/`);
  return response.data;
};

/**
 * Crear una mesa
 * @param {object} data - Datos de la mesa
 * @returns {Promise} Mesa creada
 */
export const create = async (data) => {
  const response = await POST("tables/tables/", data);
  return response.data;
};

/**
 * Actualizar una mesa
 * @param {string|number} id - ID de la mesa
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Mesa actualizada
 */
export const update = async (id, data) => {
  const response = await PUT(`tables/tables/${id}/`, data);
  return response.data;
};

/**
 * Actualizar parcialmente una mesa
 * @param {string|number} id - ID de la mesa
 * @param {object} data - Datos a actualizar
 * @returns {Promise} Mesa actualizada
 */
export const partialUpdate = async (id, data) => {
  const response = await PATCH(`tables/tables/${id}/`, data);
  return response.data;
};

/**
 * Eliminar una mesa
 * @param {string|number} id - ID de la mesa
 * @returns {Promise}
 */
export const remove = async (id) => {
  const response = await DELETE(`tables/tables/${id}/`);
  return response.data;
};

/**
 * Obtener mesas disponibles
 * @param {number} branch_id - ID de la sucursal
 * @param {number} capacity - Capacidad mínima requerida
 * @returns {Promise} Lista de mesas disponibles
 */
export const getAvailable = async (branch_id, capacity = null) => {
  let url = "tables/tables/available/";
  const params = new URLSearchParams();
  if (branch_id) params.append("branch", branch_id);
  if (capacity) params.append("capacity", capacity);
  if (params.toString()) url += `?${params.toString()}`;

  const response = await GET(url);
  return response.data;
};

/**
 * Obtener resumen de estados de mesas
 * @param {number} branch_id - ID de la sucursal
 * @returns {Promise} Resumen de estados
 */
export const getStatusSummary = async (branch_id) => {
  let url = "tables/tables/status_summary/";
  if (branch_id) url += `?branch=${branch_id}`;

  const response = await GET(url);
  return response.data;
};

/**
 * Ocupar/Liberar/Reservar una mesa
 * @param {string|number} id - ID de la mesa
 * @param {string} action - Acción: "occupy", "free", "reserve", "clean"
 * @param {object} data - Datos adicionales (order_id, estimated_duration)
 * @returns {Promise} Resultado de la acción
 */
export const occupy = async (id, action, data = {}) => {
  const response = await POST(`tables/tables/${id}/occupy/`, {
    action,
    ...data,
  });
  return response.data;
};

/**
 * Actualizar estado de múltiples mesas
 * @param {array} table_ids - IDs de las mesas
 * @param {string} status - Nuevo estado
 * @returns {Promise} Resultado
 */
export const bulkUpdateStatus = async (table_ids, status) => {
  const response = await POST("tables/tables/bulk_update_status/", {
    table_ids,
    status,
  });
  return response.data;
};

/**
 * Obtener mesas agrupadas por sucursal
 * @returns {Promise} Mesas agrupadas
 */
export const getByBranch = async () => {
  const response = await GET("tables/tables/by_branch/");
  return response.data;
};

// ==================== EXPORT ====================

const tablesApi = {
  list,
  get,
  create,
  update,
  partialUpdate,
  remove,
  getAvailable,
  getStatusSummary,
  occupy,
  bulkUpdateStatus,
  getByBranch,
};

export default tablesApi;

